import { useState } from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { CalendarIcon, Loader2, Send, Check, Star } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { submitInquiry } from "../lib/api";
import { SOCIAL } from "../data/site";

const PROJECT_TYPES = ["Brand Film", "Commercial", "Documentary", "Aerial", "Event", "Other"];
const BUDGETS = ["< $10k", "$10k – $25k", "$25k – $50k", "$50k+"];

const inputCls =
  "w-full border-0 border-b border-border bg-transparent py-3 font-body text-lg text-sand-100 placeholder:text-sand-400/60 focus:border-sand-700 focus:outline-none transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", project_type: "", budget: "", message: "" });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    const message = date ? `${form.message}\n\nPreferred start: ${format(date, "PPP")}` : form.message;
    try {
      const res = await submitInquiry({
        name: form.name,
        email: form.email,
        project_type: form.project_type || "General",
        budget: form.budget || null,
        message,
      });
      setDone(true);
      toast.success(res.email_sent ? "Sent — we'll email you back shortly." : "Received — your inquiry is saved. We'll be in touch.");
      setForm({ name: "", email: "", project_type: "", budget: "", message: "" });
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="mx-auto grid max-w-7xl gap-16 px-6 pb-32 pt-40 lg:grid-cols-12 lg:px-10 lg:pt-48">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="overline text-sand-700">Start a Project</p>
            <h1 className="mt-6 font-display text-6xl leading-[0.9] tracking-tight text-sand-100 md:text-7xl">
              Let&apos;s make something <span className="italic text-sand-700">unforgettable.</span>
            </h1>
            <p className="mt-8 max-w-md font-body text-lg font-light text-sand-400">
              Tell us about your story. We reply to every serious inquiry within two working days.
            </p>
            <div className="mt-12 space-y-4 border-t border-border pt-8">
              <p className="overline text-sand-400">Direct</p>
              <a href="mailto:Info@desertvibemedia.com" className="block font-display text-2xl text-sand-100 link-underline" data-testid="contact-email">Info@desertvibemedia.com</a>
            </div>
            <div className="mt-8 border-t border-border pt-8">
              <p className="overline text-sand-400">Worked with us?</p>
              <p className="mt-3 font-body font-light text-sand-400">
                We&apos;d be grateful if you&apos;d share your experience — real reviews help other clients find us.
              </p>
              <a
                href={SOCIAL.googleReview}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-google-review"
                className="mt-5 inline-flex items-center gap-2 border border-sand-700 px-5 py-3 overline text-sand-100 transition-colors hover:bg-sand-700 hover:text-[#0D0B0A]"
              >
                <Star size={14} fill="currentColor" /> Leave a Google review
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="flex h-full flex-col items-start justify-center border border-border bg-surface p-12"
              data-testid="contact-success"
            >
              <span className="flex h-16 w-16 items-center justify-center border border-sand-700 text-sand-700"><Check size={28} /></span>
              <h3 className="mt-8 font-display text-4xl text-sand-100">Message received.</h3>
              <p className="mt-4 font-body font-light text-sand-400">Thank you — your story matters to us. We&apos;ll be in touch soon.</p>
              <button onClick={() => setDone(false)} className="btn-desert mt-10" data-testid="contact-reset">Send another</button>
            </motion.div>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
                <div className="grid gap-10 sm:grid-cols-2">
                  <div>
                    <label className="overline text-sand-400">Name</label>
                    <input className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} data-testid="input-name" />
                  </div>
                  <div>
                    <label className="overline text-sand-400">Email</label>
                    <input type="email" className={inputCls} placeholder="you@studio.com" value={form.email} onChange={set("email")} data-testid="input-email" />
                  </div>
                </div>

                <div className="grid gap-10 sm:grid-cols-2">
                  <div>
                    <label className="overline text-sand-400">Project type</label>
                    <Select value={form.project_type} onValueChange={set("project_type")}>
                      <SelectTrigger className="mt-3 rounded-none border-0 border-b border-border bg-transparent px-0 text-lg text-sand-100 focus:ring-0" data-testid="select-project-type">
                        <SelectValue placeholder="Select a type" />
                      </SelectTrigger>
                      <SelectContent className="border-sand-600/40 bg-[#1a1512] text-sand-50 shadow-2xl">
                        {PROJECT_TYPES.map((t) => (
                          <SelectItem key={t} value={t} className="cursor-pointer py-2.5 text-base text-sand-100 focus:bg-sand-700/25 focus:text-sand-50 data-[state=checked]:text-sand-700" data-testid={`project-type-${t.toLowerCase().replace(/\s/g, "-")}`}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="overline text-sand-400">Budget</label>
                    <Select value={form.budget} onValueChange={set("budget")}>
                      <SelectTrigger className="mt-3 rounded-none border-0 border-b border-border bg-transparent px-0 text-lg text-sand-100 focus:ring-0" data-testid="select-budget">
                        <SelectValue placeholder="Select a range" />
                      </SelectTrigger>
                      <SelectContent className="border-border bg-surface text-sand-100">
                        {BUDGETS.map((b) => (
                          <SelectItem key={b} value={b} className="focus:bg-[#1d1815] focus:text-sand-700">{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="overline text-sand-400">Preferred start date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className={`mt-3 flex w-full items-center justify-between border-b border-border py-3 text-lg ${date ? "text-sand-100" : "text-sand-400/60"}`} data-testid="date-trigger">
                        {date ? format(date, "PPP") : "Pick a date"}
                        <CalendarIcon size={18} className="text-sand-700" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto border-border bg-surface p-0 text-sand-100" align="start" data-testid="date-calendar">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="overline text-sand-400">Tell us your story</label>
                  <textarea rows={4} className={`${inputCls} resize-none`} placeholder="What are we making together?" value={form.message} onChange={set("message")} data-testid="input-message" />
                </div>

                <button type="submit" disabled={loading} className="btn-desert" data-testid="submit-inquiry">
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Sending</> : <>Send inquiry <Send size={16} /></>}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
